import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { POWER_GROUPS, TOUS_SERVICES } from "../utils/taxonomy";
import { initials, PowerRequest, CreateRequestModal, RequestDetailModal, MegaphoneIcon } from "../components/PowerRequestModals";

// "Je cherche un prestataire" façon AlloVoisins : une demande publique à laquelle les
// prestataires répondent avec un devis, plutôt que de parcourir des fiches statiques. Marché
// à part, jamais mélangé au fil principal — les catégories sont celles des Powers (des
// services), pas des passions/loisirs, pour rester cohérent avec ce qu'un vrai pro propose.
export default function PowerRequests() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"nearby" | "mine">("nearby");
  const [requests, setRequests] = useState<PowerRequest[]>([]);
  const [myRequests, setMyRequests] = useState<PowerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState<PowerRequest | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(categoryFilter ? `/publications/requests/nearby?category=${encodeURIComponent(categoryFilter)}` : "/publications/requests/nearby").catch(() => ({ requests: [] })),
      user?.id ? api.get(`/users/${user.id}/publications`).catch(() => ({ publications: [] })) : Promise.resolve({ publications: [] }),
    ]).then(([nearby, mine]) => {
      setRequests(nearby?.requests || []);
      setMyRequests((mine?.publications || []).filter((p: any) => p.type === "power_request"));
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [categoryFilter]);

  const list = tab === "nearby" ? requests : myRequests;

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
            <span style={{ fontSize: 22, fontWeight: 700 }}>Demandes</span>
          </div>
          <button onClick={() => setShowCreate(true)} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Publier une demande</button>
        </div>
        <div style={{ padding: "10px 24px 0", fontSize: 13, color: "#999" }}>Tu cherches quelqu'un pour une tâche précise ? Publie une demande, les prestataires te répondent avec un devis.</div>

        <div style={{ display: "flex", gap: 8, padding: "16px 24px 0" }}>
          <button onClick={() => setTab("nearby")} style={{ padding: "8px 16px", borderRadius: 20, border: tab === "nearby" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: tab === "nearby" ? "#0a0a0a" : "white", color: tab === "nearby" ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            À proximité
          </button>
          <button onClick={() => setTab("mine")} style={{ padding: "8px 16px", borderRadius: 20, border: tab === "mine" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: tab === "mine" ? "#0a0a0a" : "white", color: tab === "mine" ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Mes demandes {myRequests.length > 0 ? `(${myRequests.length})` : ""}
          </button>
        </div>

        {tab === "nearby" && (
          <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, padding: "14px 24px 0", overflowX: "auto" }}>
            <style>{`.oltodo-no-scrollbar::-webkit-scrollbar{display:none} .oltodo-no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}`}</style>
            <button onClick={() => setCategoryFilter("")} style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: categoryFilter === "" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: categoryFilter === "" ? "#0a0a0a" : "white", color: categoryFilter === "" ? "white" : "#666" }}>
              Toutes
            </button>
            {POWER_GROUPS.filter(g => g.family !== "Autre").map(g => (
              <button key={g.family} onClick={() => setCategoryFilter(prev => prev === g.family ? "" : g.family)} style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: "1.5px solid #E0E0E0", backgroundColor: "white", color: "#666" }} title={g.items.join(", ")}>
                {g.family}
              </button>
            ))}
          </div>
        )}

        {loading && <div style={{ textAlign: "center", padding: 60, color: "#999" }}>Chargement…</div>}
        {!loading && list.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <MegaphoneIcon size={26} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{tab === "nearby" ? "Aucune demande ouverte pour l'instant" : "Tu n'as pas encore publié de demande"}</div>
          </div>
        )}

        {!loading && list.length > 0 && (
          <div style={{ padding: "16px 24px" }}>
            {list.map(r => (
              <div key={r.id} onClick={() => setDetail(r)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, overflow: "hidden", flexShrink: 0 }}>
                  {r.author_avatar ? <img src={r.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(r.author_name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#0a0a0a", backgroundColor: "#F0F0F0", borderRadius: 10, padding: "2px 8px" }}>{r.category}</span>
                    {Number(r.offers_count) > 0 && <span style={{ fontSize: 11, color: "#999" }}>{r.offers_count} devis reçu{Number(r.offers_count) > 1 ? "s" : ""}</span>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{r.title}</div>
                  <div style={{ fontSize: 12.5, color: "#666", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>{r.description}</div>
                  <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>{r.is_free ? "Budget non précisé" : `Budget indicatif : ${r.price}€`} · {r.author_name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && <CreateRequestModal categories={TOUS_SERVICES} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {detail && <RequestDetailModal request={detail} isOwner={detail.user_id === user?.id} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}
